import { ReceiptSetting } from "../../packing-items/models/packing-item";
import { ProductTag } from "../../product-tags/model";

export type ProductBarcode = {
  value: string;
  type: "EAN";
};

export enum ProductClassification {
  Generic = "generic",
  Addition = "production_addition",
  VoucherStack = "product_pack",
  VoucherOffer = "voucher_type",
  Delivery = "delivery",
  DeliveryBuffer = "delivery_buffer",
}

export const getClassificationDisplayValue = (
  classification: ProductClassification
) => {
  switch (classification) {
    case ProductClassification.Generic:
      return "Main product";
    case ProductClassification.Addition:
      return "Addition product";
    case ProductClassification.VoucherStack:
      return "Voucher stack";
    case ProductClassification.VoucherOffer:
      return "Voucher offer";
    case ProductClassification.Delivery:
      return "Delivery product";
    case ProductClassification.DeliveryBuffer:
      return "Delivery buffer product";
    default:
      return classification;
  }
};

export type ProductAdditionGroup = {
  id: string;
  name: string;
  title: string;
};

export type Product = {
  id: string;
  name: string;
  title: string;
  countryCode: string;
  internalNote: string;
  productCategoryId?: string | null;
  allowChallengeProgress: boolean;
  onlinePosProductId?: string;
  description: string;
  deliverectProductPlu?: string;
  tags: ProductTag[];
  barcodes: ProductBarcode[];
  kitchen_capacity_units?: number;
  priceDescription?: string | null;
  priceAmount: number;
  weight?: number;
  servingGroupId: string | null;
  simphonyReference: string | null;
  isComboMeal: boolean | null;
  isEligibleForDiscount?: boolean;
  priceCurrency: string;
  classification: ProductClassification;
  priceDeliveryAmount: number | string;
  priceDeliveryCurrency: string;
  sortKey: number;
  isPriceDifferentiationEnabled: boolean;
  vat: number;
  vatRuleId?: string | null;
  kitchenCategoryId?: string | null;
  labels: {
    id: string;
    type: "classification" | "strength" | "info" | "allergen";
    imageUrl?: string;
    title: string;
    description: string;
  }[];
  packagingInstructions?: {
    id: string;
    amount: number;
    category: {
      name: string;
      productKitchenCategoryId: string;
    };
    name: string;
    receiptSetting: ReceiptSetting;
    title: string;
    unit: string;
  }[];
  dimensions?: {
    length: number;
    width: number;
    height: number;
  };
  requiresManualAccept?: boolean;
  alcoholPercentage?: number;
  categoryName: string;
  imageUrl?: string | null;
  additionGroups?: ProductAdditionGroup[];
  productCoolDownRulesetId: string | null;
  productPackId: string | null;
  voucherTypeId: string | null;
  acceptedPaymentTypes: string[];
  reference?: string;
  isArchived?: boolean; // TODO: Remove nullable after deploy
};

export interface ProductSuccessResponse {
  data: Product;
}

export type CreateProductPayload = {
  classification: string;
  name: string;
  title: string;
  description: string;
  tags?: string[];
  labels?: string[];
  barcodes: ProductBarcode[];
  priceAmount: number;
  priceCurrency: string;
  isEligibleForDiscount: boolean;
  priceDeliveryAmount: number | string;
  priceDeliveryCurrency: string;
  countryCode: string;
  productCoolDownRulesetId: string | null;
  productPackId: string | null;
  voucherTypeId: string | null;
  isPriceDifferentiationEnabled: boolean;
  productCategoryId?: string | null;
  imageUrl?: string;
  vat: number;
  length?: number;
  width?: number;
  weight?: number;
  acceptedPaymentTypes?: string[];
  height?: number;
  additionGroups?: string[];
  productPackagingInstructions?: {
    amount: number | string;
    id: string;
  }[];
};

// vatRuleId comes along with the loaded product. It is read, never sent: this
// admin does not move a product to another rule.
export type UpdateProductPayload = Omit<
  Product,
  "dimensions" | "additionGroups" | "packagingInstructions" | "tags" | "labels"
> & {
  length?: number;
  width?: number;
  tags?: string[];
  labels?: string[];
  height?: number;
  additionGroups?: string[];
  productPackagingInstructions: {
    amount: number | string;
    id: string;
  }[];
};

export type GetProductListActionFilters = {
  shopId?: string;
  classification?: string;
  tags?: string[];
  productCategory?: string[];
  kitchenCategory?: string[];
  name?: string;
  title?: string;
  currencies?: string[];
  countryAlpha2Codes?: string[];
};

export type ProductListItem = {
  id: string;
  name: string;
  title: string;
  _type?: string;
  categoryName: string;
  imageUrl: string;
  description: string;
  price?: {
    amount: number;
    currency: string;
  };
};
